import classNames from "classnames";
import AppIcon from "common/app-icon";
import { Organization, UserData } from "common/Database";
import { FutureDataSource, NewAppSurveyData } from "common/firebase-function-types";
import { assertNever } from "common/Support";
import {
    DataSourcesStyle,
    NewAppModalStyle,
    Panels,
    SelectSourceModalStyle,
} from "designer/apps/orgs/overview-screen/overview-screen-style";
import InputLabel from "designer/ui/smart/input-label/input-label";
import { useThunkDispatch } from "hooks/glide-redux-hooks";
import shuffle from "lodash/shuffle";
import React from "react";
import { BuilderTheme } from "sharedUI/styles";
import { withTheme } from "styled-components";
import Img from "webapp/ui/smart/img/img";
import WindowedModalV2, { WindowedModalButtonBar } from "../windowed-modal/windowed-modal";
import * as actions from "reducers/uistate";
import { DeviceFormFactor } from "common/render/form-factor";

type FutureDataSourceLink = {
    dataSource: FutureDataSource;
    link: string;
    subtitle?: string;
};

const futureDataSourceLinks: FutureDataSourceLink[] = [
    { dataSource: "AirTable", link: "/images/airtable.png" },
    { dataSource: "MySQL", link: "/images/mysql.png" },
    { dataSource: "Excel", link: "/images/excel.png" },
    { dataSource: "CSV", link: "/images/csv.png" },
    { dataSource: "Salesforce", link: "/images/salesforce.png" },
    { dataSource: "BigQuery", link: "/images/bigquery.png" },
    { dataSource: "Redshift", link: "/images/redshift.png" },
    { dataSource: "GraphQL", link: "/images/graphql.png" },
    { dataSource: "Workday", link: "/images/workday.png" },
];

interface NewAppModalProps {
    onModalCancel(): void;
    onNewApp(org: Organization | undefined, appName: string | undefined, newAppSurveyData: NewAppSurveyData): void;
    onNewGlideTableApp(
        org: Organization | undefined,
        newApp: string | undefined,
        newAppSurveyData: NewAppSurveyData
    ): void;
    org?: Organization;
    userData: UserData;
    readonly theme: BuilderTheme;
}

type NewAppState = "appNameAndFormFactor" | "pickSource";

const NewAppModal: React.FunctionComponent<NewAppModalProps> = ({
    onModalCancel,
    onNewApp,
    onNewGlideTableApp,
    userData,
    org,
}) => {
    const [selectedModalOption, setSelectedModalOption] = React.useState<"glide" | "google">("google");
    const [showNewAppModal, setShowNewAppModal] = React.useState<NewAppState>("appNameAndFormFactor");
    const [otherSources, setOtherSources] = React.useState<string | undefined>(undefined);
    const [otherSourcesSelected, setOtherSourcesSelected] = React.useState<boolean>(false);
    const [formFactor, setFormFactor] = React.useState<DeviceFormFactor | undefined>(DeviceFormFactor.Phone);
    const [selectedFutureSources, setSelectedFutureSources] = React.useState<Set<FutureDataSource>>(new Set());
    const firstNameSegement: string = (userData?.displayName ?? "").split(" ")[0].trim();
    const defaultNewAppName: string = firstNameSegement === "" ? "New App" : `${firstNameSegement}'s App`;
    const [newAppName, setNewAppName] = React.useState<string>(defaultNewAppName);
    const shuffledFutureDataSourceLinks = React.useState(shuffle(futureDataSourceLinks))[0];

    const dispatch = useThunkDispatch();

    const onContinuePickSource = React.useCallback(() => {
        if (formFactor === undefined) {
            return;
        }
        const otherSourcesArray: string[] = otherSources === undefined ? [] : [otherSources];
        const newAppSurveyData: NewAppSurveyData = {
            sourceUsed: selectedModalOption,
            sourceWanted: [...selectedFutureSources, ...otherSourcesArray],
            formFactor: formFactor,
        };
        if (selectedModalOption === "google") {
            onNewApp(org, newAppName, newAppSurveyData);
        } else if (selectedModalOption === "glide") {
            onNewGlideTableApp(org, newAppName, newAppSurveyData);
        } else {
            assertNever(selectedModalOption);
        }
        onModalCancel();
    }, [
        formFactor,
        newAppName,
        onNewApp,
        onNewGlideTableApp,
        org,
        selectedModalOption,
        selectedFutureSources,
        otherSources,
        onModalCancel,
    ]);

    const onContinueAppNameAndFormFactor = React.useCallback(() => {
        if (newAppName !== "" && formFactor !== undefined) {
            setShowNewAppModal("pickSource");
            const submittedFormFactor: DeviceFormFactor =
                formFactor === DeviceFormFactor.Desktop ? DeviceFormFactor.Tablet : formFactor;
            dispatch(actions.setDeviceFormFactor(submittedFormFactor));
        }
    }, [newAppName, formFactor, dispatch]);

    const onSelectedFutureSources = React.useCallback(
        (source: FutureDataSource) => {
            selectedFutureSources.has(source)
                ? setSelectedFutureSources(new Set([...selectedFutureSources].filter(i => i !== source)))
                : setSelectedFutureSources(new Set([source, ...selectedFutureSources]));
        },
        [selectedFutureSources]
    );

    const onPickSourceBack = React.useCallback(() => {
        setShowNewAppModal("appNameAndFormFactor");
    }, []);

    const onOtherClicked = React.useCallback(() => {
        setOtherSourcesSelected(!otherSourcesSelected);

        if (otherSources !== undefined) {
            return;
        }
        setOtherSources("");
    }, [setOtherSources, otherSources, otherSourcesSelected]);

    if (showNewAppModal === "pickSource") {
        const modalFooter = (
            <WindowedModalButtonBar
                align="split"
                buttons={[
                    {
                        title: "Back",
                        type: "secondary",
                        variant: "default",
                        onClick: onPickSourceBack,
                    },
                    {
                        title: "Continue",
                        type: "primary",
                        variant: "default",
                        onClick: onContinuePickSource,
                    },
                ]}
            />
        );
        return (
            <WindowedModalV2
                title="Select a source"
                canClose={true}
                footer={modalFooter}
                onClose={onModalCancel}
                footerTopMargin={0}
                padding={0}>
                <SelectSourceModalStyle>
                    <div className="container">
                        <div className="field">
                            <Panels>
                                <li
                                    className={classNames(
                                        "panel-button",
                                        selectedModalOption === "google" && "selected"
                                    )}
                                    onClick={() => setSelectedModalOption("google")}>
                                    <Img className="source-image" src="/images/sheets-logo.svg" />
                                    <div className="title-h2">Google Sheets</div>
                                    <div className="subtitle">Pick a Google Sheet from your Google Drive.</div>
                                </li>
                                <li
                                    className={classNames(
                                        "panel-button",
                                        selectedModalOption === "glide" && "selected"
                                    )}
                                    onClick={() => setSelectedModalOption("glide")}>
                                    <Img className="source-image" src="/images/glide-icon.svg" />
                                    <div className="title-h2">Glide Tables</div>
                                    <div className="subtitle">
                                        Glide’s built-in data source, no Google Sheet required.
                                    </div>
                                </li>
                            </Panels>
                        </div>

                        <div className="field">
                            <div className="field-label">
                                <h3 className="title-h3">
                                    While you’re here, what other source would you want us to add?
                                </h3>
                                <span className="subtitle">Select all that apply</span>
                            </div>
                            <DataSourcesStyle className="data-sources">
                                {shuffledFutureDataSourceLinks.map(
                                    ({ dataSource, link, subtitle }: FutureDataSourceLink) => (
                                        <li
                                            className={classNames(
                                                "panel-button tight",
                                                selectedFutureSources.has(dataSource) && "selected"
                                            )}
                                            onClick={() => onSelectedFutureSources(dataSource)}
                                            key={dataSource}>
                                            <Img className="source-image" src={link} />
                                            <div className="title-h3">{dataSource}</div>
                                            {subtitle && <div className="subtitle">{subtitle}</div>}
                                        </li>
                                    )
                                )}
                                <li
                                    className={classNames(
                                        "panel-button tight",
                                        otherSourcesSelected !== false && "selected"
                                    )}
                                    onClick={onOtherClicked}>
                                    <AppIcon icon="editPencil2" size={24} />
                                    <div className="grouping">
                                        <div className="title-h3">Other</div>
                                        <div className="subtitle">Please specify</div>
                                    </div>
                                </li>
                            </DataSourcesStyle>
                            {otherSourcesSelected !== false && (
                                <div className="field">
                                    <label className="label">Other sources</label>
                                    <InputLabel
                                        margin={8}
                                        autoFocus={true}
                                        focusBorder={true}
                                        label={undefined}
                                        placeholder={"Write other sources you'd like us to add..."}
                                        value={otherSources}
                                        onChange={e => setOtherSources(e.target.value)}
                                        tallerVariation={true}
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                </SelectSourceModalStyle>
            </WindowedModalV2>
        );
    } else if (showNewAppModal === "appNameAndFormFactor") {
        const modalFooter = (
            <WindowedModalButtonBar
                align="right"
                buttons={[
                    {
                        title: "Continue",
                        type: "primary",
                        variant: "default",
                        fullSize: true,
                        disabled: newAppName === "" || formFactor === undefined,
                        onClick: onContinueAppNameAndFormFactor,
                    },
                ]}
            />
        );
        return (
            <WindowedModalV2
                title="Create a new app"
                canClose={true}
                footer={modalFooter}
                onClose={onModalCancel}
                padding={48}
                barsPadding={48}
                footerTopMargin={0}
                footerDivider={false}
                bigHeaderSize={true}>
                <NewAppModalStyle>
                    <div className="field">
                        <label className="field-label label">App Name</label>
                        <InputLabel
                            margin={0}
                            autoFocus={true}
                            focusBorder={true}
                            label={undefined}
                            placeholder={"App Name"}
                            value={newAppName}
                            onChange={e => setNewAppName(e.target.value)}
                            tallerVariation={true}
                            selectOnFocus={true}
                        />
                        <small>You can change this later</small>
                    </div>

                    <h3 className="field-label label">This app will be used primarily on</h3>
                    <Panels className="grid-3">
                        <li
                            className={classNames("panel-button centered", formFactor === "phone" && "selected")}
                            onClick={() => setFormFactor(DeviceFormFactor.Phone)}>
                            <AppIcon icon="formFactorPhone" size={28} />
                            <div className="label">Phone</div>
                        </li>
                        <li
                            className={classNames("panel-button centered", formFactor === "tablet" && "selected")}
                            onClick={() => setFormFactor(DeviceFormFactor.Tablet)}>
                            <AppIcon icon="formFactorTablet" size={28} />
                            <div className="label">Tablet</div>
                        </li>
                        <li
                            className={classNames("panel-button centered", formFactor === "desktop" && "selected")}
                            onClick={() => setFormFactor(DeviceFormFactor.Desktop)}>
                            <AppIcon icon="formFactorDesktop" size={28} />
                            <div className="label">Desktop</div>
                        </li>
                    </Panels>
                    <small>You can always preview your app on any platform.</small>
                </NewAppModalStyle>
            </WindowedModalV2>
        );
    } else {
        assertNever(showNewAppModal);
    }
};

export default withTheme(NewAppModal);
